import React, { useState, useEffect } from "react";
import { Mail, ArrowLeft, Sun, Moon, Loader2, KeyRound, CheckCircle2, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function ForgotPassword() {
  const [email, setEmail]       = useState("");
  const [isLoading, setLoading] = useState(false);
  const [apiSuccess, setSuccess]= useState("");
  const [apiError, setApiError] = useState("");
  const [theme, setTheme]       = useState(
    () => typeof window !== "undefined" ? localStorage.getItem("theme") || "light" : "light"
  );
  const D = theme === "dark";

  useEffect(() => {
    const root = window.document.documentElement;
    D ? root.classList.add("dark") : root.classList.remove("dark");
    localStorage.setItem("theme", theme);
  }, [theme, D]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setApiError(""); setSuccess("");
    if (!email) { setApiError("Please enter your email address."); return; }
    setLoading(true);
    try {
      const base = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");
      const res  = await fetch(`${base}/api/password-reset/request/`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      let data; try { data = await res.json(); } catch { data = {}; }
      if (res.ok) { setSuccess("A reset link has been sent to your email."); setEmail(""); }
      else setApiError(data.message || "Failed to process request.");
    } catch { setApiError("Connection error — check your internet and try again."); }
    finally { setLoading(false); }
  };

  // Tokens
  const bg      = D ? "#0d1f3c"                : "#f8fafc";
  const panelBg = D ? "#142a4d"                : "#ffffff";
  const border  = D ? "rgba(255,255,255,0.10)" : "#e2e8f0";
  const text    = D ? "#f0f4f8"                : "#0f172a";
  const textMid = D ? "rgba(240,244,248,0.75)" : "#475569";
  const textSoft= D ? "rgba(240,244,248,0.50)" : "#94a3b8";
  const inputBg = D ? "rgba(255,255,255,0.06)" : "#f8fafc";
  const amber   = "#f59e0b";
  const amberL  = "#fbbf24";
  const sideBg  = "linear-gradient(160deg,#0d1f3c 0%,#1a3358 55%,#0f2d56 100%)";

  const inp = {
    width:"100%", padding:"11px 11px 11px 40px", borderRadius:11,
    border:`1.5px solid ${border}`, background:inputBg, color:text,
    fontSize:14, outline:"none", fontFamily:"inherit",
    transition:"border-color .15s", boxSizing:"border-box",
  };

  return (
    <div style={{ minHeight:"100vh", display:"flex", fontFamily:"'Inter',sans-serif", background:bg, transition:"background .3s" }}>

      {/* Left brand panel */}
      <div style={{ flex:"0 0 400px", maxWidth:400, background:sideBg, flexDirection:"column", padding:"48px 40px", position:"relative", overflow:"hidden" }}
        className="hidden lg:flex">
        <div aria-hidden style={{ position:"absolute", inset:0, backgroundImage:"radial-gradient(rgba(255,255,255,0.07) 1px,transparent 1px)", backgroundSize:"24px 24px", pointerEvents:"none" }}/>
        <div aria-hidden style={{ position:"absolute", top:"-10%", left:"-10%", width:300, height:300, borderRadius:"50%", background:"radial-gradient(circle,rgba(251,191,36,0.18) 0%,transparent 70%)", pointerEvents:"none" }}/>

        <Link to="/" style={{ textDecoration:"none", display:"inline-flex", alignItems:"center", gap:10, marginBottom:"auto" }}>
          <div style={{ width:40, height:40, borderRadius:10, background:amber, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:900, fontSize:19, color:"#0d1f3c", flexShrink:0 }}>P</div>
          <span style={{ fontSize:19, fontWeight:800, color:"#fff" }}>Pilot<span style={{ color:amberL }}>Link</span> <span style={{ fontSize:11, fontWeight:600, color:"rgba(255,255,255,0.45)" }}>USA</span></span>
        </Link>

        <div style={{ position:"relative", zIndex:1, marginTop:52, marginBottom:44 }}>
          <div style={{ fontSize:32, marginBottom:16 }}>🔑</div>
          <h1 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:30, fontWeight:900, color:"#fff", lineHeight:1.2, marginBottom:16 }}>
            Forgot your<br/>password?
          </h1>
          <p style={{ fontSize:14, color:"rgba(255,255,255,0.65)", lineHeight:1.8 }}>
            No problem. Enter your email and we'll send you a secure reset link. The link expires in 5 minutes.
          </p>
        </div>

        <div style={{ position:"relative", zIndex:1, display:"flex", flexDirection:"column", gap:12 }}>
          {[["🔒","Secure 256-bit encrypted link"],["⚡","Reset in under 2 minutes"],["✅","Works for all account types"]].map(([icon,txt]) => (
            <div key={txt} style={{ display:"flex", alignItems:"center", gap:10, fontSize:13, color:"rgba(255,255,255,0.70)" }}>
              <span>{icon}</span>{txt}
            </div>
          ))}
        </div>
      </div>

      {/* Right panel */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:"100vh" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"20px 24px 0" }}>
          <Link to="/" style={{ display:"flex", alignItems:"center", gap:7, textDecoration:"none", color:textMid, fontSize:13, fontWeight:600 }}>
            <ArrowLeft size={15}/> Back to Home
          </Link>
          <button onClick={() => setTheme(D?"light":"dark")} aria-label="Toggle theme"
            style={{ width:36, height:36, borderRadius:9, border:`1px solid ${border}`, background:inputBg, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:textMid }}>
            {D ? <Sun size={15}/> : <Moon size={15}/>}
          </button>
        </div>

        <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", padding:"32px 24px" }}>
          <div style={{ width:"100%", maxWidth:400 }}>

            {/* Mobile logo */}
            <div className="lg:hidden" style={{ display:"flex", alignItems:"center", gap:9, marginBottom:28 }}>
              <div style={{ width:34, height:34, borderRadius:8, background:amber, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:900, fontSize:15, color:"#0d1f3c" }}>P</div>
              <span style={{ fontSize:16, fontWeight:800, color:text }}>Pilot<span style={{ color:amber }}>Link</span></span>
            </div>

            {apiSuccess ? (
              /* ── SUCCESS STATE ── */
              <div style={{ background:panelBg, border:`1px solid ${border}`, borderRadius:18, padding:"36px 28px", textAlign:"center", boxShadow:D?"0 4px 32px rgba(0,0,0,0.25)":"0 4px 24px rgba(13,31,60,0.08)" }}>
                <div style={{ width:64, height:64, borderRadius:"50%", background:"rgba(34,197,94,0.15)", border:"2px solid rgba(34,197,94,0.30)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px", fontSize:28 }}>✉️</div>
                <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:22, fontWeight:900, color:text, marginBottom:10 }}>Check your inbox</h2>
                <p style={{ fontSize:14, color:textMid, lineHeight:1.7, marginBottom:24 }}>
                  We sent a password reset link to <strong style={{ color:text }}>{email || "your email"}</strong>. Check your spam folder if you don't see it within a minute.
                </p>
                <div style={{ padding:"12px 16px", borderRadius:12, background:D?"rgba(251,191,36,0.08)":"#fffbeb", border:`1px solid ${D?"rgba(251,191,36,0.20)":"#fde68a"}`, fontSize:13, color:D?"#fbbf24":"#92400e", marginBottom:24, lineHeight:1.6 }}>
                  ⏱ The reset link expires in <strong>5 minutes</strong> for your security.
                </div>
                <Link to="/login" style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6, width:"100%", padding:"13px", borderRadius:11, background:`linear-gradient(135deg,${amber},${amberL})`, color:"#0d1f3c", fontWeight:800, fontSize:15, textDecoration:"none", boxShadow:"0 4px 16px rgba(245,158,11,0.38)" }}>
                  <ArrowLeft size={16}/> Back to Sign In
                </Link>
              </div>
            ) : (
              /* ── REQUEST FORM ── */
              <>
                <div style={{ marginBottom:28 }}>
                  <div style={{ fontSize:28, marginBottom:12 }}>🔑</div>
                  <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:26, fontWeight:900, color:text, marginBottom:6 }}>Forgot Password?</h2>
                  <p style={{ fontSize:14, color:textMid }}>Enter your account email and we'll send you a reset link.</p>
                </div>

                <div style={{ background:panelBg, border:`1px solid ${border}`, borderRadius:18, padding:"28px 26px", boxShadow:D?"0 4px 32px rgba(0,0,0,0.25)":"0 4px 24px rgba(13,31,60,0.08)" }}>
                  {apiError && (
                    <div style={{ marginBottom:16, padding:"11px 14px", borderRadius:10, background:"rgba(251,113,133,0.10)", border:"1px solid rgba(251,113,133,0.25)", fontSize:13, color:"#fda4af", fontWeight:500 }}>
                      ⚠ {apiError}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} noValidate>
                    <div style={{ marginBottom:20 }}>
                      <label style={{ display:"block", fontSize:12, fontWeight:700, color:textMid, marginBottom:7, letterSpacing:".03em" }}>Email Address</label>
                      <div style={{ position:"relative" }}>
                        <Mail size={15} style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", color:textSoft, pointerEvents:"none" }}/>
                        <input type="email" value={email} placeholder="you@example.com" autoComplete="email" required
                          onChange={e => { setEmail(e.target.value); setApiError(""); }}
                          onFocus={e => e.target.style.borderColor=D?"rgba(251,191,36,0.60)":"#f59e0b"}
                          onBlur={e  => e.target.style.borderColor=border}
                          style={inp}/>
                      </div>
                    </div>

                    <button type="submit" disabled={isLoading} style={{
                      width:"100%", padding:"13px", borderRadius:11, border:"none",
                      cursor:isLoading?"not-allowed":"pointer",
                      background:isLoading?"rgba(245,158,11,0.55)":`linear-gradient(135deg,${amber},${amberL})`,
                      color:"#0d1f3c", fontSize:15, fontWeight:800,
                      display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                      boxShadow:isLoading?"none":"0 4px 16px rgba(245,158,11,0.38)",
                      fontFamily:"inherit", transition:"all .15s",
                    }}
                    onMouseEnter={e=>{ if(!isLoading) e.currentTarget.style.filter="brightness(1.06)"; }}
                    onMouseLeave={e=>{ e.currentTarget.style.filter="none"; }}>
                      {isLoading
                        ? <><Loader2 size={17} style={{ animation:"spin 1s linear infinite" }}/> Sending Reset Link…</>
                        : <>Send Reset Link <ChevronRight size={17}/></>}
                    </button>
                  </form>

                  <div style={{ marginTop:18, paddingTop:16, borderTop:`1px solid ${border}`, textAlign:"center" }}>
                    <Link to="/login" style={{ fontSize:13, fontWeight:600, color:textMid, textDecoration:"none", display:"inline-flex", alignItems:"center", gap:5 }}>
                      <ArrowLeft size={13}/> Back to Sign In
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div style={{ padding:"14px 24px", textAlign:"center", fontSize:12, color:textSoft, borderTop:`1px solid ${border}` }}>
          © {new Date().getFullYear()} PilotLink USA. All rights reserved.
        </div>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
    </div>
  );
}
